/**
 * Full-screen photo viewer. Opens over the grid, closes on Escape, the
 * backdrop, or the close button; arrows and swipes move between photos.
 */

import { icon } from '../js/icons.js'
import { text } from '../js/html.js'

let teardown = null

export function openLightbox(items, startIndex) {
  closeLightbox()

  let index = startIndex

  const overlay = document.createElement('div')
  overlay.className = 'lightbox'
  overlay.setAttribute('role', 'dialog')
  overlay.setAttribute('aria-modal', 'true')
  overlay.setAttribute('aria-label', 'Photo')
  document.body.append(overlay)
  document.body.classList.add('is-locked')

  function draw() {
    const item = items[index]

    overlay.innerHTML = `
      <button class="lightbox__close" aria-label="Close">${icon('x', { size: 24 })}</button>

      <figure class="lightbox__figure">
        <img class="lightbox__image" src="${text(item.full_url)}" alt="${text(item.caption)}"
             style="background-image:url(${text(item.blur)})" />
        ${item.caption ? `<figcaption class="lightbox__caption">${text(item.caption)}</figcaption>` : ''}
      </figure>

      <nav class="lightbox__bar">
        <button class="lightbox__nav" data-step="-1" aria-label="Previous"
                ${index === 0 ? 'disabled' : ''}>${icon('chevronLeft', { size: 22 })}</button>
        <span class="lightbox__count">${index + 1} / ${items.length}</span>
        <a class="lightbox__nav" href="${text(item.full_url)}?download=1" download
           aria-label="Download">${icon('download', { size: 20 })}</a>
        <button class="lightbox__nav" data-step="1" aria-label="Next"
                ${index === items.length - 1 ? 'disabled' : ''}>${icon('chevronRight', { size: 22 })}</button>
      </nav>
    `

    overlay.querySelector('.lightbox__close').addEventListener('click', closeLightbox)
    overlay.querySelectorAll('[data-step]').forEach((button) =>
      button.addEventListener('click', () => step(Number(button.dataset.step))),
    )
  }

  function step(by) {
    const next = index + by

    if (next >= 0 && next < items.length) {
      index = next
      draw()
    }
  }

  function onKey(event) {
    if (event.key === 'Escape') closeLightbox()
    if (event.key === 'ArrowLeft') step(-1)
    if (event.key === 'ArrowRight') step(1)
  }

  // Swiping is how this is actually used; the arrows are for desktop.
  let startX = null
  const onTouchStart = (event) => {
    startX = event.changedTouches[0].clientX
  }
  const onTouchEnd = (event) => {
    if (startX === null) return

    const delta = event.changedTouches[0].clientX - startX
    if (Math.abs(delta) > 60) step(delta < 0 ? 1 : -1)
    startX = null
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) closeLightbox()
  })
  overlay.addEventListener('touchstart', onTouchStart, { passive: true })
  overlay.addEventListener('touchend', onTouchEnd, { passive: true })
  document.addEventListener('keydown', onKey)

  teardown = () => {
    document.removeEventListener('keydown', onKey)
    overlay.remove()
    document.body.classList.remove('is-locked')
    teardown = null
  }

  draw()
}

export function closeLightbox() {
  teardown?.()
}
